import 'styled-components';
import { Flex, Row } from '@keyslabor/shared/ui';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate } from '@/hooks/useNavigate.ts';
import LayoutPageMobile from '@/components/LayoutPageMobile';
import RecordButton from './RecordButton';
import TopTextBlock from '@/components/TopTextBlock';
import {
  usePostAnswersConfirmation,
  usePostAnswersUploadRejected,
} from '@/api/services';
import { routes } from '@/constants';
import Preloader from '@/components/Preloader';
import { isDevPreview } from '@/utils';
import * as Sentry from '@sentry/react';
import useGetQuestionWithAnswered from '@/hooks/useGetQuestionWithAnswered';
import useCountdown from '@/hooks/useTimer';
import { VIDEO_MAX_LENGTH } from './constants';
import theme from '@keyslabor/shared/theme';
import VideoReplay from './VideoReplay';
import VideoRecord from './VideoRecord';
import { useTranslation } from 'react-i18next';
import retry from 'async-retry';
import { QuestionsAnswersInput } from '@/api/types.ts';
import { FaceFrame } from '@/pages/Answers/FaceFrame.tsx';
import { useVideoOffsetTop } from '@/pages/Answers/useVideoOffsetTop';
import useUploadRetryModal from '@/components/useUploadRetryModal';
import uploadVideoAnswer from '@/pages/Answers/uploadVideoAnswer.ts';
import LandscapeBlock from './LandscapeBlock.tsx';

// ── Constants ────────────────────────────────────────────────────────────────

const UPLOAD_RETRY_DELAY = 3000;
const UPLOAD_RETRY_ATTEMPTS = 3;
const UPLOAD_SUCCESS_REDIRECT_DELAY = 3000;
const UPLOAD_ABORT_TIMEOUT = 60; // seconds

// ── Typed error wrapper (replaces @ts-expect-error casts) ────────────────────

class UploadError extends Error {
  responseStatus?: number;
  responseText?: string;

  constructor(message: string) {
    super(message);
    this.name = 'UploadError';
  }
}

function toUploadError(error: unknown): UploadError {
  if (error instanceof UploadError) return error;
  if (error instanceof Error) {
    const wrapped = new UploadError(error.message);
    wrapped.stack = error.stack;
    return wrapped;
  }
  return new UploadError('Unknown upload error');
}

// ── Centralized logging ──────────────────────────────────────────────────────

function logAndCapture(message: string, error: unknown) {
  console.error(message, error);
  Sentry.captureException(error instanceof Error ? error : new Error(message));
}

// ── Fake upload for dev preview ──────────────────────────────────────────────

const resolveFakeUpload = (retryAttempt: number): Promise<Response> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (retryAttempt === 1) {
        resolve({
          ok: true,
          status: 200,
          text: () => Promise.resolve(''),
        } as Response);
      } else {
        reject(new Error('fail'));
      }
    }, 3000);
  });
};

// ── State reducer ────────────────────────────────────────────────────────────

type AnswerState = {
  isRecording: boolean;
  isRecordReady: boolean;
  isUploading: boolean;
  showRetryConfirm: boolean;
  recordReplaySrc: string | null;
  videoRecordDataWebm: Blob | undefined;
};

type AnswerAction =
  | { type: 'START_RECORDING' }
  | { type: 'STOP_RECORDING' }
  | { type: 'RECORD_READY'; replaySrc: string; blob: Blob }
  | { type: 'RESET_RECORD' }
  | { type: 'SET_UPLOADING'; value: boolean }
  | { type: 'SHOW_RETRY_CONFIRM' }
  | { type: 'HIDE_RETRY_CONFIRM' };

const initialState: AnswerState = {
  isRecording: false,
  isRecordReady: false,
  isUploading: false,
  showRetryConfirm: false,
  recordReplaySrc: null,
  videoRecordDataWebm: undefined,
};

function answerReducer(state: AnswerState, action: AnswerAction): AnswerState {
  switch (action.type) {
    case 'START_RECORDING':
      return { ...state, isRecording: true };
    case 'STOP_RECORDING':
      return { ...state, isRecording: false };
    case 'RECORD_READY':
      return {
        ...state,
        isRecordReady: true,
        recordReplaySrc: action.replaySrc,
        videoRecordDataWebm: action.blob,
      };
    case 'RESET_RECORD':
      return {
        ...state,
        isRecording: false,
        isRecordReady: false,
        recordReplaySrc: null,
        videoRecordDataWebm: undefined,
      };
    case 'SET_UPLOADING':
      return { ...state, isUploading: action.value };
    case 'SHOW_RETRY_CONFIRM':
      return { ...state, showRetryConfirm: true };
    case 'HIDE_RETRY_CONFIRM':
      return { ...state, showRetryConfirm: false };
    default:
      return state;
  }
}

// ── Responsive camera width hook ─────────────────────────────────────────────

function useCameraWidth() {
  const breakpointSm = Number(theme.breakpoints.sm);
  const [width, setWidth] = useState(() =>
    Math.min(window.innerWidth, breakpointSm)
  );

  useEffect(() => {
    const handleResize = () =>
      setWidth(Math.min(window.innerWidth, breakpointSm));

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpointSm]);

  return width;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function Answers() {
  const [state, dispatch] = useReducer(answerReducer, initialState);
  const {
    isRecording,
    isRecordReady,
    isUploading,
    showRetryConfirm,
    recordReplaySrc,
    videoRecordDataWebm,
  } = state;

  const navigate = useNavigate();
  const { t } = useTranslation();
  const cameraWidth = useCameraWidth();
  const [retryModal, triggerRetryModal] = useUploadRetryModal({
    uploadTimeout: UPLOAD_ABORT_TIMEOUT,
  });

  const videoRef = useRef<HTMLVideoElement>(null);
  const videoOffsetTop = useVideoOffsetTop(videoRef);
  const mediaRecordRef = useRef<MediaRecorder | null>(null);

  // Stabilise the stop callback so the timer never triggers a stale reference
  const stopRecordRef = useRef<() => void>(() => undefined);

  const {
    questionName,
    invitationKey,
    isLoading,
    data,
    selectedLanguage,
    questionNumber,
    totalQuestions,
    questionTopics,
    onNextQuestion,
  } = useGetQuestionWithAnswered();

  const { mutateAsync: confirmAnswer } = usePostAnswersConfirmation();
  const { mutateAsync: rejectAnswer } = usePostAnswersUploadRejected();

  const isLastQuestion = questionNumber >= totalQuestions - 1;
  const isIntroQuestion = questionNumber === 0;
  const answerPresignedUrl = data?.answer_presigned_url;
  const isShowRecord = isRecording || !isRecordReady;

  // ── Timer (uses ref so we never recreate the countdown) ──────────────────

  const timer = useCountdown({
    initTime: VIDEO_MAX_LENGTH,
    onTimeout: () => stopRecordRef.current(),
  });

  // ── Recording handlers ──────────────────────────────────────────────────

  const handleStartRecord = useCallback(() => {
    if (!mediaRecordRef.current) {
      alert('Video camera is not available. Please provide permissions');
      return;
    }
    if (mediaRecordRef.current.state === 'inactive') {
      mediaRecordRef.current.start();
      dispatch({ type: 'START_RECORDING' });
      timer.reset();
      timer.startCountdown();
    }
  }, [timer]);

  const handleStopRecord = useCallback(() => {
    if (!mediaRecordRef.current) return;
    mediaRecordRef.current.stop();
    dispatch({ type: 'STOP_RECORDING' });
    timer.stop();
  }, [timer]);

  // Keep ref in sync
  useEffect(() => {
    stopRecordRef.current = handleStopRecord;
  }, [handleStopRecord]);

  const handleResetRecord = useCallback(() => {
    dispatch({ type: 'RESET_RECORD' });
  }, []);

  // ── Callbacks fed to VideoRecord (stable via dispatch) ───────────────────

  const handleRecordReady = useCallback(
    (replaySrc: string, blob: Blob) => {
      dispatch({ type: 'RECORD_READY', replaySrc, blob });
    },
    []
  );

  // ── Upload ──────────────────────────────────────────────────────────────

  const handleUploadAnswer = useCallback(async (): Promise<boolean> => {
    if (!videoRecordDataWebm || !answerPresignedUrl) {
      logAndCapture(
        'No video record data or answer presigned url',
        new Error('No video record data or answer presigned url')
      );
      return false;
    }

    dispatch({ type: 'SET_UPLOADING', value: true });

    const confirmationPayload: QuestionsAnswersInput = {
      invitation_key: invitationKey,
      question_num: questionNumber,
      language: selectedLanguage,
    } as QuestionsAnswersInput;

    let uploadedFirstAttempt = true;
    const videoSizeBytes = videoRecordDataWebm.size;
    const uploadStartMs = performance.now();

    Sentry.addBreadcrumb({
      category: 'upload',
      message: 'Video upload started',
      level: 'info',
      data: {
        questionNumber,
        videoSizeBytes,
        videoSizeMB: +(videoSizeBytes / (1024 * 1024)).toFixed(2),
      },
    });

    try {
      const result = await Sentry.startSpan(
        {
          name: 'video-answer.upload',
          op: 'http.upload',
          attributes: {
            'upload.question_number': questionNumber,
            'upload.video_size_bytes': videoSizeBytes,
            'upload.max_retries': UPLOAD_RETRY_ATTEMPTS,
            'upload.timeout_sec': UPLOAD_ABORT_TIMEOUT,
          },
        },
        async (uploadSpan) => {
          let lastAttempt = 0;

          await retry(
            async (_bail: unknown, attempt: number) => {
              lastAttempt = attempt;

              if (attempt > 1) {
                uploadedFirstAttempt = false;
                triggerRetryModal({ variant: 'processing', retryAttempt: attempt });
              }

              const attemptStartMs = performance.now();

              Sentry.addBreadcrumb({
                category: 'upload',
                message: `Upload attempt ${attempt}`,
                level: 'info',
                data: { attempt, questionNumber },
              });

              const fetchPromise = isDevPreview
                ? resolveFakeUpload(attempt)
                : uploadVideoAnswer(
                    answerPresignedUrl,
                    videoRecordDataWebm,
                    UPLOAD_ABORT_TIMEOUT
                  );

              const res = await fetchPromise.catch((e: unknown) => {
                const attemptDurationMs = performance.now() - attemptStartMs;

                Sentry.addBreadcrumb({
                  category: 'upload',
                  message: `Upload attempt ${attempt} network error`,
                  level: 'error',
                  data: {
                    attempt,
                    attemptDurationMs: +attemptDurationMs.toFixed(0),
                    error: e instanceof Error ? e.message : String(e),
                  },
                });

                logAndCapture('Failed to upload video to presigned url', e);
                triggerRetryModal({ variant: 'retry', retryAttempt: attempt });
                throw e;
              });

              const attemptDurationMs = performance.now() - attemptStartMs;

              if (!res.ok) {
                const err = new UploadError(
                  'Failed to upload video to presigned url'
                );
                err.responseStatus = res.status;
                err.responseText = await res.text();

                Sentry.addBreadcrumb({
                  category: 'upload',
                  message: `Upload attempt ${attempt} failed (HTTP ${res.status})`,
                  level: 'error',
                  data: {
                    attempt,
                    statusCode: res.status,
                    attemptDurationMs: +attemptDurationMs.toFixed(0),
                  },
                });

                throw err;
              }

              Sentry.addBreadcrumb({
                category: 'upload',
                message: `Upload attempt ${attempt} succeeded`,
                level: 'info',
                data: {
                  attempt,
                  statusCode: res.status,
                  attemptDurationMs: +attemptDurationMs.toFixed(0),
                  uploadSpeedMBps: +(
                    videoSizeBytes /
                    (1024 * 1024) /
                    (attemptDurationMs / 1000)
                  ).toFixed(2),
                },
              });
            },
            {
              retries: UPLOAD_RETRY_ATTEMPTS,
              factor: 1,
              minTimeout: UPLOAD_RETRY_DELAY,
            }
          );

          const totalDurationMs = performance.now() - uploadStartMs;

          uploadSpan?.setAttributes({
            'upload.total_attempts': lastAttempt,
            'upload.first_attempt_success': uploadedFirstAttempt,
            'upload.total_duration_ms': +totalDurationMs.toFixed(0),
            'upload.upload_speed_mbps': +(
              videoSizeBytes /
              (1024 * 1024) /
              (totalDurationMs / 1000)
            ).toFixed(2),
          });

          return true;
        }
      );

      if (!result) return false;

      if (!uploadedFirstAttempt) {
        await new Promise((r) => setTimeout(r, UPLOAD_SUCCESS_REDIRECT_DELAY));
      }

      triggerRetryModal((prev) =>
        prev ? { variant: 'success', retryAttempt: 0 } : null
      );

      await confirmAnswer(confirmationPayload).catch((e: unknown) => {
        logAndCapture('Failed to post answer confirmation', e);
      });

      return true;
    } catch (e: unknown) {
      const uploadErr = toUploadError(e);
      const totalDurationMs = performance.now() - uploadStartMs;

      Sentry.addBreadcrumb({
        category: 'upload',
        message: 'Video upload failed after all retries',
        level: 'error',
        data: {
          questionNumber,
          totalDurationMs: +totalDurationMs.toFixed(0),
          videoSizeBytes,
          responseStatus: uploadErr.responseStatus,
        },
      });

      triggerRetryModal((prev) =>
        prev ? { variant: 'error', retryAttempt: 0 } : null
      );
      rejectAnswer(confirmationPayload).catch(() => {});

      Sentry.withScope((scope) => {
        scope.setContext('submissionDetails', {
          invitationKey,
          selectedLanguage,
          answerPresignedUrl,
          questionNumber,
          videoPutStatusCode: uploadErr.responseStatus,
          videoPutText: uploadErr.responseText,
        });
        Sentry.captureException(uploadErr);
      });

      return false;
    } finally {
      dispatch({ type: 'SET_UPLOADING', value: false });
    }
  }, [
    videoRecordDataWebm,
    answerPresignedUrl,
    invitationKey,
    questionNumber,
    selectedLanguage,
    triggerRetryModal,
    confirmAnswer,
    rejectAnswer,
  ]);

  // ── Navigation ──────────────────────────────────────────────────────────

  const handleNextStep = useCallback(async () => {
    if (!isRecordReady) return;

    const ok = await handleUploadAnswer();
    if (!ok) return;

    if (isLastQuestion) {
      navigate(`${routes.finish}?turn_off_handle_routing=1`);
      return;
    }

    onNextQuestion(questionNumber + 1);
    navigate(routes.questions);
  }, [
    isRecordReady,
    isLastQuestion,
    handleUploadAnswer,
    navigate,
    onNextQuestion,
    questionNumber,
  ]);

  // ── Submit button label ──────────────────────────────────────────────────

  const submitButtonContent = isUploading ? (
    <Flex gap="10px" flexCenter>
      <span>Uploading...</span> <Preloader size="25px" />
    </Flex>
  ) : isLastQuestion ? (
    t('answers.submit_and_finish')
  ) : (
    t('answers.submit_and_continue')
  );

  // ── Header ───────────────────────────────────────────────────────────────

  const headerTitle = isIntroQuestion
    ? t('answers.topics')
    : `${t('answers.topics_for_question')} #${questionName}`;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <LayoutPageMobile
      header={
        <Row flexCenter>
          <TopTextBlock title={headerTitle} content={questionTopics} />
        </Row>
      }
      loading={isLoading}
    >
      <LandscapeBlock onRecordDisable={handleStopRecord} />

      <Row
        flexCenter
        position="relative"
        flexGrow={1}
        zIndex="50"
        height="100%"
        alignContent="flex-end"
      >
        {!isShowRecord ? (
          <VideoReplay src={recordReplaySrc} width={cameraWidth} />
        ) : (
          <>
            <VideoRecord
              ref={videoRef}
              cameraWidth={cameraWidth}
              mediaRecordRef={mediaRecordRef}
              setIsRecordReady={(ready: boolean) => {
                if (ready) return; // handled by onRecordReady
                dispatch({ type: 'RESET_RECORD' });
              }}
              setRecordReplaySrc={(src: string | null) => {
                // kept for backwards compat; primary path is onRecordReady
                if (src === null) dispatch({ type: 'RESET_RECORD' });
              }}
              setVideoRecordDataWebm={() => {
                // no-op: blob set via onRecordReady
              }}
              onRecordReady={handleRecordReady}
            />
            {isIntroQuestion && <FaceFrame offsetTop={videoOffsetTop} />}
          </>
        )}
      </Row>

      <Row flexCenter>
        <RecordButton
          onShowRetryConfirm={() => dispatch({ type: 'SHOW_RETRY_CONFIRM' })}
          isShowRetryConfirm={showRetryConfirm}
          isRecordReady={isRecordReady}
          isRecording={isRecording}
          onNextStep={handleNextStep}
          onRecordStart={handleStartRecord}
          onRecordStop={handleStopRecord}
          onRetryRecord={handleResetRecord}
          onHideRetryConfirm={() => dispatch({ type: 'HIDE_RETRY_CONFIRM' })}
          timeLeft={!isRecordReady ? timer.time : 0}
        >
          {submitButtonContent}
        </RecordButton>
      </Row>

      {retryModal}
    </LayoutPageMobile>
  );
}
